import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import s from './QuietLuxury.module.css';

import img01 from '../../images/הבית של זוהר 2.webp';
import img02 from '../../images/הבית של סיון 17.webp';
import img03 from '../../images/הבית של גילה 1.webp';
import img04 from '../../images/הבית של גל בנחלה 1.webp';
import img05 from '../../images/הבית של זוהר 16.webp';
import img06 from '../../images/הבית של גילה 12.webp';
import img07 from '../../images/הבית של הילה בנחלה 3.webp';
import img08 from '../../images/הבית של זוהר 11.webp';

const IMAGES = [
  img01, img02, img03, img04,
  img05, img06, img07, img08,
];

export default function QuietLuxury({ onOpenLead }) {
  const sectionRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(null);
  const isOpen = activeIdx !== null;

  const openLightbox = useCallback((i) => setActiveIdx(i), []);
  const closeLightbox = useCallback(() => setActiveIdx(null), []);
  const next = useCallback(
    () => setActiveIdx((i) => (i === null ? null : (i + 1) % IMAGES.length)),
    []
  );
  const prev = useCallback(
    () => setActiveIdx((i) =>
      i === null ? null : (i - 1 + IMAGES.length) % IMAGES.length
    ),
    []
  );

  /* Stagger reveal on scroll */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const targets = el.querySelectorAll('[data-reveal]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(s.visible);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  /* Lightbox keyboard + scroll-lock */
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') prev();  // RTL: right = previous
      else if (e.key === 'ArrowLeft') next();   // RTL: left = next
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closeLightbox, next, prev]);

  return (
    <section className={s.section} ref={sectionRef}>
      <h2 className={s.headline} data-reveal>
        אז איך נראית <em className={s.headlineEm}>יוקרה שקטה</em> בבית?
      </h2>

      <div className={s.grid}>
        {IMAGES.map((src, i) => (
          <figure
            key={i}
            className={s.frame}
            style={{ '--delay': `${(i % 4) * 0.1}s` }}
            data-reveal
            onClick={() => openLightbox(i)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openLightbox(i);
              }
            }}
          >
            <div className={s.clip}>
              <img src={src} alt="" loading="lazy" />
            </div>
            <span className={s.expand} aria-hidden="true">
              <span />
              <span />
            </span>
          </figure>
        ))}
      </div>

      {/* CTA — framed like the images, wider */}
      <button
        type="button"
        className={s.cta}
        onClick={onOpenLead}
        data-reveal
      >
        <span className={s.ctaFill} aria-hidden="true" />
        <span className={s.ctaText}>לשיחת היכרות לחצו כאן</span>
        <span className={s.ctaArrow} aria-hidden="true">←</span>
      </button>

      {/* Lightbox — rendered via portal to escape stacking contexts */}
      {isOpen && createPortal(
        <div
          className={s.lightbox}
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLightbox();
          }}
        >
          <div className={s.lightboxBackdrop} />

          <button
            type="button"
            className={s.lightboxClose}
            onClick={closeLightbox}
            aria-label="סגירה"
          >
            <span />
            <span />
          </button>

          <button
            type="button"
            className={`${s.lightboxNav} ${s.lightboxPrev}`}
            onClick={prev}
            aria-label="הקודם"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <button
            type="button"
            className={`${s.lightboxNav} ${s.lightboxNext}`}
            onClick={next}
            aria-label="הבא"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className={s.lightboxStage}>
            <div className={s.lightboxFrame} key={activeIdx}>
              <img src={IMAGES[activeIdx]} alt="" />
            </div>
            <div className={s.lightboxCounter}>
              <span className={s.lightboxCounterNum}>
                {String(activeIdx + 1).padStart(2, '0')}
              </span>
              <span className={s.lightboxCounterSep}>/</span>
              <span className={s.lightboxCounterTotal}>
                {String(IMAGES.length).padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
